"use client";

import React from "react";
import { MapPin, Clock, PhoneCall, ShieldCheck, Wrench, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, Navigation } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const coverageZones = [{
  name: "Downtown & Historic Jonesboro",
  zip: "30236",
  timing: "20-35 mins",
  desc: "Fast emergency dispatch across Main Street, courthouse district, and adjacent residential streets."
}, {
  name: "Tara Boulevard / US-19 Corridor",
  zip: "30236 / 30238",
  timing: "25-40 mins",
  desc: "Complete commercial and residential plumbing service along Tara Blvd and surrounding retail corridors."
}, {
  name: "Lake Spivey & South Clayton",
  zip: "30236",
  timing: "30-45 mins",
  desc: "Emergency water line repairs, leak detection, and water heater replacements for lakeside homes."
}, {
  name: "Flint River & Arrowhead",
  zip: "30238",
  timing: "25-45 mins",
  desc: "Full coverage for urgent drain clearing, sewer main backups, and slab leaks."
}];
const serviceHighlights = [{
  title: "Burst Pipe & Flooding Control",
  desc: "Immediate shut-off assistance, leak isolation, and high-pressure pipe repair."
}, {
  title: "Sewer & Main Drain Backups",
  desc: "Hydro-jetting and camera inspection to clear hazardous drain line blockages."
}, {
  title: "Water Heater Breakdown",
  desc: "Same-day repair or emergency replacement for tankless and standard units."
}, {
  title: "Slab & Hidden Leak Detection",
  desc: "Non-invasive electronic acoustic testing to pinpoint foundation leaks."
}];
const quickMetrics = [{
  icon: Clock,
  value: "24/7 / 365",
  label: "Always Open"
}, {
  icon: Navigation,
  value: "< 45 Min",
  label: "Avg. Arrival Time"
}, {
  icon: ShieldCheck,
  value: "100%",
  label: "Licensed & Insured"
}, {
  icon: CheckCircle2,
  value: "0$",
  label: "Hidden Travel Fees"
}];
export default function JonesboroArea() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="serviceareajonesboro" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} variants={containerVariants} className="flex flex-col items-center text-center max-w-3xl mx-auto mb-14">
          <motion.div variants={itemVariants} className="mb-3">
            <Badge variant="outline" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold border-primary/20 bg-primary/5 text-primary">
              <MapPin className="size-3 text-primary mr-1" />
              Clayton County Local Dispatch
            </Badge>
          </motion.div>
          
          <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl md:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Jonesboro, GA Emergency Plumbing Coverage
          </motion.h2>
          
          <motion.p variants={itemVariants} className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Based right here in Jonesboro, our mobile emergency plumbing units are stationed locally to reach your home or business fast when sudden leaks, sewer overflows, or water heater failures strike.
          </motion.p>
        </motion.div>

        {/* Quick Stats Grid */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }} variants={containerVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {quickMetrics.map((metric, i) => {
          const Icon = metric.icon;
          return <motion.div key={metric.label} data-index={i} variants={itemVariants} className="bg-card text-card-foreground p-5 rounded-xl border border-border flex flex-col items-center text-center shadow-xs">
                <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                  <Icon className="size-5" />
                </div>
                <div className="text-2xl font-bold font-default text-foreground">{metric.value}</div>
                <div className="text-xs sm:text-sm text-muted-foreground font-medium mt-1">{metric.label}</div>
              </motion.div>;
        })}
        </motion.div>

        {/* Main Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          {/* Left Column: Local Zones */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground">Jonesboro Neighborhoods We Cover</h3>
                <p className="text-sm text-muted-foreground mt-1">Priority dispatch with localized routing for fast response.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {coverageZones.map((zone, i) => <Card key={zone.name} data-index={i} className="border border-border shadow-xs hover:border-primary/40 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base font-semibold text-foreground">{zone.name}</CardTitle>
                      <Badge variant="secondary" className="text-2xs font-mono shrink-0">{zone.zip}</Badge>
                    </div>
                    <CardDescription className="flex items-center gap-1.5 text-xs text-primary font-medium mt-1">
                      <Clock className="size-3.5" />
                      Typical Response: {zone.timing}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-xs text-muted-foreground leading-relaxed">
                    {zone.desc}
                  </CardContent>
                </Card>)}
            </div>

            {/* Coverage note banner */}
            <div className="bg-muted text-muted-foreground p-4 rounded-xl border border-border flex items-start gap-3 text-sm">
              <AlertTriangle className="size-5 text-accent shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-foreground">Nearby Service Note: </span>
                We also serve immediate surrounding areas including Morrow, Riverdale, Forest Park, and Stockbridge for major plumbing emergencies.
              </div>
            </div>
          </div>

          {/* Right Column: Key Emergency Services in Jonesboro */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <Card className="border border-border shadow-sm bg-card">
              <CardHeader className="pb-4 border-b border-border">
                <CardTitle className="text-lg font-bold text-card-foreground flex items-center gap-2">
                  <Wrench className="size-5 text-primary" />
                  Urgent Jonesboro Plumbing Repairs
                </CardTitle>
                <CardDescription className="text-xs">
                  Equipped trucks ready to troubleshoot and repair on the first visit.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4 flex flex-col gap-4">
                {serviceHighlights.map((svc, i) => <div key={svc.title} data-index={i} className="flex items-start gap-3">
                    <div className="size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle2 className="size-3.5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">{svc.title}</h4>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-normal">{svc.desc}</p>
                    </div>
                  </div>)}
              </CardContent>
            </Card>

            {/* Direct Contact Card */}
            <div className="bg-primary text-primary-foreground p-6 rounded-xl shadow-md flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <span className="relative flex size-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                  <span className="relative inline-flex rounded-full size-3 bg-accent"></span>
                </span>
                <span className="text-xs uppercase tracking-wider font-bold text-accent">Active Dispatch Unit On Duty</span>
              </div>
              <div>
                <h4 className="text-lg font-bold text-primary-foreground">Plumbing Problem in Jonesboro Right Now?</h4>
                <p className="text-xs text-primary-foreground/80 mt-1 leading-relaxed">
                  Do not let standing water damage your flooring or drywall. Speak directly with an on-call emergency plumber.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <Button asChild variant="default" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold w-full sm:w-auto shadow-sm">
                  <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                    <PhoneCall className="size-4" />
                    <span>Call (470) 847-9076</span>
                  </a>
                </Button>
                <Button asChild variant="outline" className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 w-full sm:w-auto">
                  <Link to="/contact/">Request Service</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        {/* Footer Navigation Hints */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <MapPin className="size-4 text-primary" />
            <span>Serving Jonesboro, GA 30236 & 30238 with 24-hour plumbing dispatch</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/services/" className="text-primary hover:underline font-medium">All Plumbing Services</Link>
            <span>&bull;</span>
            <Link to="/water-heater/" className="text-primary hover:underline font-medium">Water Heater Repairs</Link>
            <span>&bull;</span>
            <Link to="/problems/" className="text-primary hover:underline font-medium">Common Problems</Link>
          </div>
        </div>
      </div>
    </section>;
}